import { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ThumbsRating } from "@/components/thumbs-rating";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthContext } from "@/contexts/AuthContext";
import { useStationAccess } from "@/hooks/useStationAccess";
import AppLayout from "@/components/layout/app-layout";
import { StationToolLayout } from "@/components/stations/station-tool-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { FlippableFactCard } from "@/components/flippable-fact-card";
import { FactDetailView } from "@/components/fact-detail-view";
import { ArrowLeft, Sparkles, Building2, Globe, Globe2, Target, AlertCircle, CheckCircle2, Loader2, FileText, Search, Copy, RefreshCw, ChevronDown, ChevronUp, Mail, Database, Check, Hash, Filter } from "lucide-react";
import thinkingAnimation from "@assets/Analytical Thinking Illustration_1757593408862.gif";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { Station, VbrRequest } from "@shared/schema";

const vbrFormSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  companyWebsite: z.string()
    .optional()
    .or(z.literal(""))
    .refine((value) => {
      // Allow empty strings
      if (!value || value === "") return true;
      
      // Check if it's a valid URL with protocol
      try {
        new URL(value);
        return true;
      } catch {
        // If not, check if it's a valid domain without protocol
        try {
          new URL(`https://${value}`);
          return true;
        } catch {
          return false;
        }
      }
    }, "Please enter a valid website (e.g., example.com or https://example.com)"),
  industry: z.string().optional(),
  targetAudience: z.string().optional(),
  painPoints: z.string().optional(),
  additionalContext: z.string().optional(),
});

type VBRFormValues = z.infer<typeof vbrFormSchema>;

interface GeneratedVBR {
  vbrText: string;
  primaryFact: {
    fact: string;
    source: string | { text: string; url: string };
  };
  alternativeFacts: Array<{
    fact: string;
    source: string | { text: string; url: string };
  }>;
  citations: string[];
}

export default function VBREngine() {
  usePageTitle("VBR Engine");
  const [location, navigate] = useLocation();
  const { stationId } = useParams<{ stationId: string }>();
  const { hasAccess } = useStationAccess(stationId);
  const { toast } = useToast();
  const { getAuthToken } = useAuthContext();
  const [discoveredFacts, setDiscoveredFacts] = useState<Array<{ 
    fact: string; 
    source: string | { text: string; url: string };
    sourceType?: string;
    sourceQueueId?: string;
    sourceData?: any;
    metadata?: any;
  }>>([]);
  const [generatedVBR, setGeneratedVBR] = useState<{ vbrText: string; citations: string[] } | null>(null);
  const [currentVBRText, setCurrentVBRText] = useState<string>("");
  const [selectedFact, setSelectedFact] = useState<{ 
    fact: string; 
    source: string | { text: string; url: string };
    sourceType?: string;
    sourceQueueId?: string;
    sourceData?: any;
    metadata?: any;
  } | null>(null);
  const [isFormMinimized, setIsFormMinimized] = useState(false);
  const [isFineTuningOpen, setIsFineTuningOpen] = useState(false);
  const [isFactsMinimized, setIsFactsMinimized] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string>("");
  const [step, setStep] = useState<'input' | 'facts' | 'vbr'>('input');
  const [currentStage, setCurrentStage] = useState(0);
  const [telemetryData, setTelemetryData] = useState({
    keywordsExtracted: 0,
    documentsSearched: 0,
    factsFound: 0,
    factsFiltered: 0,
    searchSpeed: 0,
    relevanceScore: 0
  });
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneText, setMilestoneText] = useState("");
  const [modelInfo, setModelInfo] = useState<{
    modelUsed?: string;
    reasoningEffort?: string;
    fallbackUsed?: boolean;
  } | null>(null);
  const [vbrModelInfo, setVbrModelInfo] = useState<{
    modelUsed?: string;
    reasoningEffort?: string;
    fallbackUsed?: boolean;
  } | null>(null);
  
  // State for fact detail view
  const [viewingFactDetail, setViewingFactDetail] = useState<{ 
    fact: { 
      fact: string; 
      source: string | { text: string; url: string };
      sourceType?: string;
      sourceQueueId?: string;
      sourceData?: any;
      metadata?: any;
    };
    index: number;
  } | null>(null);
  
  // State for fact detail view in VBR step
  const [viewingVbrFactDetail, setViewingVbrFactDetail] = useState<{ 
    fact: { 
      fact: string; 
      source: string | { text: string; url: string };
      sourceType?: string;
      sourceQueueId?: string;
      sourceData?: any;
      metadata?: any;
    };
    index: number;
  } | null>(null);
  
  // Ref for auto-resizing textarea
  const vbrTextareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Ref to track abort controller for in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Track the stationId that initiated the current request
  const requestStationIdRef = useRef<string | null>(null);
  
  // Auto-resize textarea when content changes
  useEffect(() => {
    const textarea = vbrTextareaRef.current;
    if (textarea && currentVBRText) {
      // Reset height to auto to get proper scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [currentVBRText]);

  // Reset all state when stationId changes (switching between stations)
  useEffect(() => {
    // Abort any in-flight requests from the previous station
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear the request station ref to signal station change
    requestStationIdRef.current = null;
    
    // Reset discovered facts and VBR state
    setDiscoveredFacts([]);
    setGeneratedVBR(null);
    setCurrentVBRText("");
    setSelectedFact(null);
    
    // Reset UI state
    setIsFormMinimized(false);
    setIsFineTuningOpen(false);
    setIsFactsMinimized(false);
    setLoadingStep("");
    setStep('input');
    setCurrentStage(0);
    
    // Reset telemetry
    setTelemetryData({
      keywordsExtracted: 0,
      documentsSearched: 0,
      factsFound: 0,
      factsFiltered: 0,
      searchSpeed: 0,
      relevanceScore: 0
    });
    
    // Reset milestone and model info
    setShowMilestone(false);
    setMilestoneText("");
    setModelInfo(null);
    setVbrModelInfo(null);
    
    // Reset fact detail views
    setViewingFactDetail(null);
    setViewingVbrFactDetail(null);
    
    // Reset regeneration state
    setIsRegenerating(false);
    setOriginalFormValues(null);
    
    // Reset form fields
    form.reset({
      companyName: "",
      companyWebsite: "",
      industry: "",
      targetAudience: "",
      painPoints: "",
      additionalContext: "",
    });
  }, [stationId]);

  // Fetch station details
  const { data: station, isLoading: stationLoading } = useQuery<Station>({
    queryKey: [`/api/stations/${stationId}`],
    enabled: !!stationId,
  });

  const form = useForm<VBRFormValues>({
    resolver: zodResolver(vbrFormSchema),
    defaultValues: {
      companyName: "",
      companyWebsite: "",
      industry: "",
      targetAudience: "",
      painPoints: "",
      additionalContext: "",
    },
  });

  // State for regeneration loading
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [originalFormValues, setOriginalFormValues] = useState<VBRFormValues | null>(null);

  // Step 1: Discover facts mutation with SSE streaming
  const discoverFacts = useMutation<{ facts: Array<{ 
    fact: string; 
    source: string | { text: string; url: string };
    sourceType?: string;
    sourceQueueId?: string;
    sourceData?: any;
    metadata?: any;
  }>; modelInfo?: { modelUsed?: string; reasoningEffort?: string; fallbackUsed?: boolean } }, Error, VBRFormValues>({
    mutationFn: async (data: VBRFormValues) => {
      // Store the form values for later VBR generation
      setOriginalFormValues(data);
      
      // Track which station this request is for
      const requestStationId = stationId;
      requestStationIdRef.current = requestStationId;
      
      // Reset telemetry
      setTelemetryData({
        keywordsExtracted: 0,
        documentsSearched: 0,
        factsFound: 0,
        factsFiltered: 0,
        searchSpeed: 0,
        relevanceScore: 0
      });
      
      // Get Firebase token for authentication
      const token = await getAuthToken();
      
      // Retry logic with exponential backoff
      const maxRetries = 3;
      let lastError: Error | null = null;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        // Check if station changed before each attempt
        if (requestStationIdRef.current !== requestStationId) {
          throw new Error('Station changed - request cancelled');
        }
        
        // Initialize startTime outside try block so it's accessible in catch
        const startTime = Date.now();
        
        try {
          // Show retry status if not first attempt
          if (attempt > 1) {
            setLoadingStep(`Request timed out, retrying... (attempt ${attempt}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, attempt - 1), 5000))); // Exponential backoff
          }
          
          const headers: HeadersInit = {
            'Content-Type': 'application/json',
          };
          
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          // Create an AbortController with 60-second timeout
          // Store in ref so it can be aborted on station switch
          const controller = new AbortController();
          abortControllerRef.current = controller;
          const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds
          
          const response = await fetch(`/api/stations/${stationId}/vbr/discover-facts`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(data),
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          abortControllerRef.current = null;
          
          if (!response.ok) {
            throw new Error('Failed to discover facts');
          }
          
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) throw new Error('No reader available');

          let buffer = '';
          let factsResult: any = null;
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6).trim();
                if (data === '[DONE]') continue;
                
                try {
                  const parsed = JSON.parse(data);
              
              if (parsed.type === 'progress') {
                // Map progress stages to UI stages
                let stage = 1;
                switch(parsed.stage) {
                  case 'Company Analysis Complete':
                    stage = 2;
                    setLoadingStep("Extracting search keywords...");
                    break;
                  case 'Searching Facts':
                    stage = 3;
                    setLoadingStep("Searching knowledge bases...");
                    break;
                  case 'Facts Found':
                    stage = 5;
                    setLoadingStep("Processing discovered facts...");
                    break;
                  case 'AI Filtering':
                    stage = 6;
                    setLoadingStep("Filtering relevant facts...");
                    break;
                  case 'Facts Ready':
                    stage = 7;
                    setLoadingStep("Finalizing results...");
                    break;
                  default:
                    setLoadingStep(parsed.stage || "Processing...");
                }
                setCurrentStage(stage);
                
                // Update telemetry if we have fact counts
                if (parsed.detail && parsed.detail.includes('Found')) {
                  const matches = parsed.detail.match(/\d+/g);
                  if (matches) {
                    const totalFacts = matches.reduce((sum: number, n: string) => sum + parseInt(n), 0);
                    setTelemetryData(prev => ({ 
                      ...prev, 
                      factsFound: totalFacts
                    }));
                  }
                }
              } else if (parsed.type === 'complete') {
                factsResult = parsed;
              } else if (parsed.type === 'error') {
                throw new Error(parsed.message);
              }
            } catch (e) {
              if (e instanceof Error && !e.message.includes('JSON')) {
                throw e;
              }
                }
              }
            }
          }
          
          if (!factsResult) {
            throw new Error('No facts received from server');
          }
          
          // Check if station changed during processing
          if (requestStationIdRef.current !== requestStationId) {
            throw new Error('Station changed - request cancelled');
          }
          
          // Final completion stage
          setCurrentStage(8);
          setLoadingStep("Processing complete!");
          await new Promise(resolve => setTimeout(resolve, 500));
      
          // Update telemetry with actual results
          setTelemetryData(prev => ({ 
            ...prev, 
            factsFiltered: factsResult.facts.length
          }));
          
          // Return result with the station ID so onSuccess can verify
          return { ...factsResult, _requestStationId: requestStationId };
          
        } catch (error: any) {
          lastError = error;
          const duration = Date.now() - startTime;
          
          // Check if this was a station-switch abort - don't retry in this case
          if (requestStationIdRef.current !== requestStationId) {
            throw new Error('Station changed - request cancelled');
          }
          
          // Log timeout errors to API Status page
          if (error.name === 'AbortError' || error.message.includes('timeout')) {
            try {
              const logHeaders: HeadersInit = { 'Content-Type': 'application/json' };
              if (token) logHeaders['Authorization'] = `Bearer ${token}`;
              
              await fetch('/api/log-client-error', {
                method: 'POST',
                headers: logHeaders,
                credentials: 'include',
                body: JSON.stringify({
                  endpoint: `/api/stations/${stationId}/vbr/discover-facts`,
                  errorMessage: `Request timeout after ${Math.round(duration / 1000)}s (attempt ${attempt}/${maxRetries})`,
                  statusCode: 408, // 408 Request Timeout - proper HTTP code for timeout
                  duration,
                  apiType: 'internal',
                  metadata: {
                    attemptNumber: attempt,
                    maxRetries,
                    companyName: data.companyName,
                    isTimeout: true
                  }
                }),
              });
            } catch (logError) {
              console.error('Failed to log timeout error:', logError);
            }
            
            // Continue to next retry attempt if not at max
            if (attempt < maxRetries) {
              continue;
            }
          }
          
          // For non-timeout errors or last attempt, throw the error
          throw error;
        }
      }
      
      // If we've exhausted all retries, throw the last error
      throw lastError || new Error('Failed to discover facts after multiple attempts');
    },
    onSuccess: async (data: any) => {
      // Check if station changed during the request - compare station ID from request with current
      if (data._requestStationId !== stationId) {
        console.log('Station changed during discovery - ignoring stale results');
        return;
      }
      
      console.log('Discovered Facts:', data.facts);
      
      // Extract and store model info if available
      if (data.modelInfo) {
        console.log('Model Info:', data.modelInfo);
        setModelInfo(data.modelInfo);
      }
      
      // Sort facts: station first, then global, then web
      const sortedFacts = [...data.facts].sort((a, b) => {
        const getSourceType = (source: string | { text: string; url: string }) => {
          if (typeof source === 'object' && source.url) return 3; // Web facts last
          if (typeof source === 'string') {
            if (source.includes('NPR Profile') || source.includes('Nielsen')) return 1; // Station facts first
            return 2; // Global facts in the middle
          }
          return 4; // Unknown
        };
        
        return getSourceType(a.source) - getSourceType(b.source);
      });
      
      // Facts are ready to display
      
      setDiscoveredFacts(sortedFacts);
      setStep('facts');
      setIsFormMinimized(true);
      setLoadingStep("");
      setCurrentStage(0);
      
      // Show success milestone if we found good facts
      if (data.facts.length >= 5) {
        setMilestoneText(`${data.facts.length} relevant facts ready!`);
        setShowMilestone(true);
        setTimeout(() => setShowMilestone(false), 2000);
      }
      
      if (data.facts.length === 0) {
        toast({
          title: "No relevant facts found",
          description: "Try adjusting your search criteria or adding more details.",
          variant: "destructive",
        });
      } else if (data.facts.length < 6) {
        // Fewer facts than typical target, but that's OK - quality over quantity
        toast({
          title: "Facts discovered",
          description: `Found ${data.facts.length} highly relevant facts. Quality over quantity - select one to generate your VBR.`,
        });
      } else {
        toast({
          title: "Facts discovered",
          description: `Found ${data.facts.length} relevant facts. Select one to generate your VBR.`,
        });
      }
    },
    onError: (error: Error) => {
      // Check if station changed during the request - if so, don't show error
      if (requestStationIdRef.current === null || error.message.includes('Station changed')) {
        console.log('Station changed during discovery - suppressing error');
        return;
      }
      
      setLoadingStep("");
      setCurrentStage(0);
      setIsFormMinimized(false);
      // Reset telemetry on error
      setTelemetryData({
        keywordsExtracted: 0,
        documentsSearched: 0,
        factsFound: 0,
        factsFiltered: 0,
        searchSpeed: 0,
        relevanceScore: 0
      });
      toast({
        title: "Discovery Failed",
        description: error.message || "Failed to discover facts. Please try again.",
        variant: "destructive",
      });
    },
  });

  async function onSubmit(data: VBRFormValues) {
    discoverFacts.mutate(data);
    setIsFormMinimized(true);
  }

  // Step 2: Generate VBR with selected fact
  const handleFactSelection = async (fact: { fact: string; source: string | { text: string; url: string } }) => {
    if (!originalFormValues) return;
    
    setSelectedFact(fact);
    setIsRegenerating(true);
    setIsFactsMinimized(true); // Collapse facts section
    
    // Update progress stages for VBR generation
    setCurrentStage(1);
    setLoadingStep("Processing your selected fact...");
    await new Promise(resolve => setTimeout(resolve, 200));
    
    setCurrentStage(2);
    setLoadingStep("Generating personalized content...");
    await new Promise(resolve => setTimeout(resolve, 200));
    
    setCurrentStage(3);
    setLoadingStep("Formatting your VBR email...");
    
    try {
      // Call the regeneration endpoint to generate VBR with selected fact
      const response = await apiRequest(
        `/api/stations/${stationId}/vbr/regenerate`,
        "POST",
        {
          input: originalFormValues,
          selectedFact: fact
        }
      );
      
      // Store the generated VBR
      setCurrentStage(4);
      setLoadingStep("Finalizing your VBR email...");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Extract and store model info if available
      if (response.modelInfo) {
        console.log('VBR Model Info:', response.modelInfo);
        setVbrModelInfo(response.modelInfo);
      }
      
      setGeneratedVBR(response);
      setCurrentVBRText(response.vbrText);
      setStep('vbr');
      setLoadingStep("");
      setCurrentStage(0);
      
      toast({
        title: "VBR Generated Successfully",
        description: "Your personalized sales outreach has been created.",
      });
      
      // Invalidate caches to update dashboard stats, activities, and leaderboard
      queryClient.invalidateQueries({ queryKey: [`/api/stations/${stationId}/vbr`] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/activities'] });
      queryClient.invalidateQueries({ queryKey: ['/api/leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['/api/global-stats'] });
    } catch (error) {
      console.error("Error generating VBR:", error);
      setLoadingStep("");
      toast({
        title: "Generation failed",
        description: "Failed to generate VBR with the selected fact.",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };
  
  // Handle swapping facts after VBR is generated
  const handleFactSwap = async (newFact: { fact: string; source: string | { text: string; url: string } }) => {
    if (!generatedVBR || !originalFormValues) return;
    
    // Don't regenerate if clicking the already selected fact
    if (selectedFact?.fact === newFact.fact) return;
    
    await handleFactSelection(newFact);
  };

  const copyToClipboard = () => {
    // Copy the VBR text directly without adding header/signature
    navigator.clipboard.writeText(currentVBRText);
    toast({
      title: "Copied to clipboard",
      description: "Email has been copied to clipboard.",
    });
  };
  
  const handleNewVBRRequest = () => {
    // Reset all state to start over
    setStep('input');
    setIsFormMinimized(false);
    setIsFactsMinimized(false);
    setDiscoveredFacts([]);
    setSelectedFact(null);
    setGeneratedVBR(null);
    setIsRegenerating(false);
    setCurrentStage(0);
    setLoadingStep("");
    setModelInfo(null);
    setVbrModelInfo(null);
    setCurrentVBRText("");
    setShowMilestone(false);
    setMilestoneText("");
    setOriginalFormValues(null);
    form.reset({
      companyName: '',
      companyWebsite: '',
      industry: '',
      targetAudience: '',
      painPoints: '',
      additionalContext: '',
    });
  };

  if (stationLoading) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!station) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Station not found.</AlertDescription>
          </Alert>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <StationToolLayout station={station}>
        <div className="space-y-6">
        {/* New VBR Request Button - Show only when VBR is displayed */}
        {step === 'vbr' && generatedVBR && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNewVBRRequest}
            data-testid="button-new-vbr"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            New VBR Request
          </Button>
        )}
        
        {/* Input Form - Collapsible */}
        <Card className={(step !== 'input') ? "transition-all duration-300" : ""}>
          <CardHeader 
            className={(step !== 'input') ? "cursor-pointer" : ""}
            onClick={() => (step !== 'input') && setIsFormMinimized(!isFormMinimized)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Company Information</CardTitle>
                {isFormMinimized && (
                  <CardDescription className="mt-1">
                    {form.getValues('companyName')} • Click to edit
                  </CardDescription>
                )}
              </div>
              {(step !== 'input') && (
                <Button variant="ghost" size="sm">
                  {isFormMinimized ? 
                    <ChevronDown className="h-4 w-4" /> : 
                    <ChevronUp className="h-4 w-4" />
                  }
                </Button>
              )}
            </div>
          </CardHeader>
          {!isFormMinimized && (
            <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="e.g., Acme Corporation" 
                          {...field}
                          data-testid="input-company-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyWebsite"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Website</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="example.com or https://example.com" 
                          {...field}
                          data-testid="input-company-website"
                        />
                      </FormControl>
                      <FormDescription>
                        Enter a website address (with or without http://)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Fine Tuning Section - Collapsible */}
                <Collapsible open={isFineTuningOpen} onOpenChange={setIsFineTuningOpen}>
                  <CollapsibleTrigger asChild>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="w-full justify-between p-0 h-auto font-medium text-base hover:bg-transparent"
                      data-testid="button-fine-tuning-toggle"
                    >
                      <span>Fine Tuning</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isFineTuningOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 mt-4">
                    <FormField
                      control={form.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Industry (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="e.g., Technology, Healthcare, Finance" 
                              {...field}
                              data-testid="input-industry"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="targetAudience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target Audience (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="e.g., B2B SaaS companies, Enterprise clients" 
                              {...field}
                              data-testid="input-target-audience"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="painPoints"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Known Pain Points (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe the challenges they're facing..."
                              className="min-h-[100px]"
                              {...field}
                              data-testid="input-pain-points"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="additionalContext"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Additional Context (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Any other relevant information..."
                              className="min-h-[80px]"
                              {...field}
                              data-testid="input-additional-context"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CollapsibleContent>
                </Collapsible>

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={discoverFacts.isPending}
                  data-testid="button-discover-facts"
                >
                  {discoverFacts.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Finding Relevant Facts...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Find Relevant Facts
                    </>
                  )}
                </Button>
              </form>
            </Form>
            </CardContent>
          )}
        </Card>

        {/* Loading State - Show while discovering facts or generating VBR */}
        {(discoverFacts.isPending || isRegenerating) && (
          <Card className="shadow-xl border-2 border-blue-400">
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center space-y-6">
                {/* Animated GIF with milestone animation */}
                <div className="relative">
                  <img 
                    src={thinkingAnimation} 
                    alt="Processing" 
                    className="w-[150px] h-[150px] object-contain"
                  />
                  {showMilestone && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-green-500 text-white px-4 py-2 rounded-full animate-pulse-scale shadow-lg">
                        <span className="font-bold text-sm">{milestoneText}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Show progress bar for fact discovery */}
                {discoverFacts.isPending && (
                  <div className="w-full max-w-2xl px-8">
                    <div className="relative">
                      {/* Progress Line */}
                      <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full"></div>
                      <div 
                        className="absolute top-5 left-0 h-1 bg-blue-500 rounded-full transition-all duration-1000"
                        style={{ 
                          width: `${(currentStage / 7) * 100}%`
                        }}
                      ></div>
                      
                      {/* Fact Discovery Stage Indicators */}
                      <div className="relative flex justify-between">
                        {[
                          { label: 'Analyzing', icon: Search },
                          { label: 'Keywords', icon: Hash },
                          { label: 'Station', icon: Building2 },
                          { label: 'Global', icon: Globe },
                          { label: 'Web', icon: Globe2 },
                          { label: 'Filtering', icon: Filter },
                          { label: 'Finalizing', icon: CheckCircle2 }
                        ].map((stage, index) => (
                          <div key={index} className="flex flex-col items-center">
                            <div className={`
                              w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300
                              ${currentStage > index + 1 
                                ? 'bg-blue-500 border-blue-500 text-white' 
                                : currentStage === index + 1
                                  ? 'bg-white border-blue-500 text-blue-500 scale-110 shadow-lg'
                                  : 'bg-gray-100 border-gray-300 text-gray-400'}
                            `}>
                              <stage.icon className="w-5 h-5" />
                            </div>
                            <span className={`
                              text-xs mt-2 font-medium transition-all duration-300
                              ${currentStage > index + 1
                                ? 'text-blue-600'
                                : currentStage === index + 1
                                  ? 'text-blue-700 font-bold'
                                  : 'text-gray-400'}
                            `}>
                              {stage.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show progress bar for VBR generation */}
                {isRegenerating && (
                  <div className="w-full max-w-2xl px-8">
                    <div className="relative">
                      {/* Progress Line */}
                      <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full"></div>
                      <div 
                        className="absolute top-5 left-0 h-1 bg-blue-500 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(currentStage / 4) * 100}%`
                        }}
                      ></div>
                      
                      {/* VBR Generation Stage Indicators */}
                      <div className="relative flex justify-between">
                        {[
                          { label: 'Processing', icon: Target },
                          { label: 'Generating', icon: Sparkles },
                          { label: 'Formatting', icon: Mail },
                          { label: 'Finalizing', icon: CheckCircle2 }
                        ].map((stage, index) => (
                          <div key={index} className="flex flex-col items-center">
                            <div className={`
                              w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300
                              ${currentStage > index + 1 
                                ? 'bg-blue-500 border-blue-500 text-white' 
                                : currentStage === index + 1
                                  ? 'bg-blue-500 border-blue-500 text-white scale-110'
                                  : 'bg-white border-gray-300 text-gray-400'
                              }
                            `}>
                              {currentStage > index + 1 ? (
                                <Check className="h-5 w-5" />
                              ) : (
                                <stage.icon className="h-5 w-5" />
                              )}
                            </div>
                            <span className={`text-xs mt-2 ${currentStage >= index + 1 ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                              {stage.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Loading Step Text with Telemetry */}
                <div className="text-center space-y-3">
                  <p className="text-lg font-medium text-gray-800">
                    {loadingStep}
                  </p>
                  
                  {/* VBR Generation Telemetry */}
                  {isRegenerating && (
                    <div className="space-y-1">
                      {currentStage >= 1 && (
                        <p className="text-sm text-gray-600 animate-fade-in">
                          Processing fact from <span className="font-semibold text-blue-600">
                            {selectedFact?.source && typeof selectedFact.source === 'object' ? 'Web Research' : 'Knowledge Base'}
                          </span>
                        </p>
                      )}
                      {currentStage >= 2 && (
                        <p className="text-sm text-gray-600 animate-fade-in">
                          Crafting personalized message...
                        </p>
                      )}
                      {currentStage >= 3 && (
                        <p className="text-sm text-gray-600 animate-fade-in">
                          Applying professional formatting...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Show Discovered Facts */}
        {step === 'facts' && discoveredFacts.length > 0 && !isRegenerating && (
          <div className="space-y-6">
            {viewingFactDetail ? (
              // Show fact detail view
              <FactDetailView
                fact={viewingFactDetail.fact}
                index={viewingFactDetail.index}
                onBack={() => setViewingFactDetail(null)}
                onSelect={handleFactSelection}
              />
            ) : (
              // Show facts grid
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Select Your Opening Fact</CardTitle>
                      <CardDescription>
                        We found {discoveredFacts.length} relevant facts for {originalFormValues?.companyName}. 
                        Click on any fact to generate your personalized VBR.
                      </CardDescription>
                    </div>
                    {stationId && <ThumbsRating toolCategory="VBR Fact Selection" stationId={stationId} />}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {discoveredFacts.map((fact, index) => {
                      const isProcessorFact = fact.sourceType === 'tapscan' || fact.sourceType === 'profile';
                      
                      // For processor facts, show a button to view details
                      if (isProcessorFact) {
                        return (
                          <Card 
                            key={index}
                            className="hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => setViewingFactDetail({ fact, index })}
                            data-testid={`card-fact-${index}`}
                          >
                            <CardHeader className="pb-3">
                              <Badge variant="outline">Fact {index + 1}</Badge>
                            </CardHeader>
                            <CardContent>
                              <p className="text-sm font-medium mb-3">{fact.fact}</p>
                              <div className="space-y-2">
                                <div className="text-xs text-gray-500 italic">
                                  {typeof fact.source === 'string' ? (
                                    fact.source
                                  ) : (
                                    <span>{fact.source.text}</span>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewingFactDetail({ fact, index });
                                  }}
                                  data-testid={`button-view-details-${index}`}
                                >
                                  <Sparkles className="h-3 w-3 mr-2" />
                                  View Details
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      } else {
                        // Non-processor facts (web facts) - direct selection
                        return (
                          <Card 
                            key={index}
                            className="hover:shadow-lg transition-shadow"
                          >
                            <CardHeader className="pb-3">
                              <Badge variant="outline">Fact {index + 1}</Badge>
                            </CardHeader>
                            <CardContent>
                              <p className="text-sm font-medium mb-3">{fact.fact}</p>
                              <div className="space-y-2">
                                <div className="text-xs text-gray-500 italic">
                                  {typeof fact.source === 'string' ? (
                                    fact.source
                                  ) : (
                                    <a 
                                      href={fact.source.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {fact.source.text}
                                    </a>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="w-full"
                                  onClick={() => handleFactSelection(fact)}
                                  data-testid={`button-select-fact-${index}`}
                                >
                                  <Sparkles className="h-3 w-3 mr-2" />
                                  Select This Fact
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      }
                    })}
                  </div>
                  
                  {/* Model Transparency Display */}
                  {modelInfo && (
                    <div className="mt-6 text-center">
                    <p 
                      className="text-xs text-muted-foreground" 
                      data-testid="text-powered-model-facts"
                    >
                      Powered by {
                        modelInfo.modelUsed?.includes('gpt-5') ? 'GPT-5' :
                        modelInfo.modelUsed?.includes('gpt-4') ? 'GPT-4' :
                        'AI Model'
                      }
                      {modelInfo.fallbackUsed && ' (fallback)'}
                    </p>
                  </div>
                )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 3: Generated VBR Output */}
        {step === 'vbr' && generatedVBR && !isRegenerating && (
          <div className="space-y-6">
            {/* Fact Swapping Cards - Collapsible */}
            {discoveredFacts.length > 0 && (
              <Card>
                <Collapsible open={!isFactsMinimized} onOpenChange={(open) => setIsFactsMinimized(!open)}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Select a Different Opening Fact</CardTitle>
                        {isFactsMinimized ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                      {isFactsMinimized && selectedFact && (
                        <CardDescription className="mt-2">
                          Currently selected: "{selectedFact.fact.substring(0, 60)}..."
                        </CardDescription>
                      )}
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      {viewingVbrFactDetail ? (
                        // Show fact detail view
                        <FactDetailView
                          fact={viewingVbrFactDetail.fact}
                          index={viewingVbrFactDetail.index}
                          onBack={() => setViewingVbrFactDetail(null)}
                          onSelect={() => {
                            handleFactSwap(viewingVbrFactDetail.fact);
                            setViewingVbrFactDetail(null);
                          }}
                        />
                      ) : (
                        // Show facts grid
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* All Facts Cards */}
                          {discoveredFacts.map((fact, index) => {
                            const isSelected = selectedFact?.fact === fact.fact;
                            const isProcessorFact = fact.sourceType === 'tapscan' || fact.sourceType === 'profile';
                            
                            // If it's the selected fact, show it with a selected indicator
                            if (isSelected) {
                              return (
                                <Card
                                  key={index}
                                  className="border-2 border-green-500 bg-gradient-to-br from-green-50 to-white shadow-lg"
                                >
                                  <CardHeader className="pb-3">
                                    <Badge className="bg-green-600">Selected</Badge>
                                  </CardHeader>
                                  <CardContent>
                                    <p className="text-sm font-medium mb-3">{fact.fact}</p>
                                    <div className="text-xs text-gray-500 italic">
                                      {typeof fact.source === 'string' ? (
                                        fact.source
                                      ) : (
                                        <a 
                                          href={fact.source.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 underline"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {fact.source.text}
                                        </a>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            }
                            
                            // For processor facts, show a button to view details
                            if (isProcessorFact) {
                              return (
                                <Card 
                                  key={index}
                                  className="hover:shadow-lg transition-shadow cursor-pointer"
                                  onClick={() => setViewingVbrFactDetail({ fact, index })}
                                  data-testid={`card-vbr-fact-${index}`}
                                >
                                  <CardHeader className="pb-3">
                                    <Badge variant="outline">Fact {index + 1}</Badge>
                                  </CardHeader>
                                  <CardContent>
                                    <p className="text-sm font-medium mb-3">{fact.fact}</p>
                                    <div className="space-y-2">
                                      <div className="text-xs text-gray-500 italic">
                                        {typeof fact.source === 'string' ? (
                                          fact.source
                                        ) : (
                                          <span>{fact.source.text}</span>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-full"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setViewingVbrFactDetail({ fact, index });
                                        }}
                                        data-testid={`button-view-vbr-details-${index}`}
                                      >
                                        <Sparkles className="h-3 w-3 mr-2" />
                                        View Details
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            } else {
                              // Non-processor facts (web facts) - direct selection
                              return (
                                <Card 
                                  key={index}
                                  className="hover:shadow-lg transition-shadow"
                                >
                                  <CardHeader className="pb-3">
                                    <Badge variant="outline">Fact {index + 1}</Badge>
                                  </CardHeader>
                                  <CardContent>
                                    <p className="text-sm font-medium mb-3">{fact.fact}</p>
                                    <div className="space-y-2">
                                      <div className="text-xs text-gray-500 italic">
                                        {typeof fact.source === 'string' ? (
                                          fact.source
                                        ) : (
                                          <a 
                                            href={fact.source.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {fact.source.text}
                                          </a>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="default"
                                        className="w-full"
                                        onClick={() => handleFactSwap(fact)}
                                        data-testid={`button-swap-fact-${index}`}
                                      >
                                        <RefreshCw className="h-3 w-3 mr-2" />
                                        Use This Fact
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            }
                          })}
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )}

            {/* Main VBR Email Output - Prominent */}
            <Card className="shadow-xl border-2">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-xl">Your VBR Email</CardTitle>
                      <CardDescription>Ready to send personalized outreach</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {stationId && <ThumbsRating toolCategory="VBR Email Generation" stationId={stationId} />}
                    <Button
                      onClick={copyToClipboard}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="button-copy-vbr"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Email
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-white rounded-lg border p-6 font-sans">
                  {/* VBR Content - Editable textarea */}
                  <textarea
                    ref={vbrTextareaRef}
                    value={currentVBRText}
                    onChange={(e) => setCurrentVBRText(e.target.value)}
                    className="w-full min-h-[300px] text-gray-800 leading-relaxed resize-none border-0 focus:outline-none focus:ring-0 p-0 bg-transparent"
                    style={{ 
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: '1.625',
                    }}
                    placeholder="Your VBR will appear here..."
                    data-testid="textarea-vbr-content"
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Model Transparency Display for VBR - Outside the Card */}
            {vbrModelInfo && (
              <div className="text-center mt-4">
                <p 
                  className="text-xs text-muted-foreground" 
                  data-testid="text-powered-model-vbr"
                >
                  Powered by {
                    vbrModelInfo.modelUsed?.includes('gpt-5') ? 'GPT-5' :
                    vbrModelInfo.modelUsed?.includes('gpt-4') ? 'GPT-4' :
                    'AI Model'
                  }
                  {vbrModelInfo.fallbackUsed && ' (fallback)'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
      </div>
      </StationToolLayout>
    </AppLayout>
  );
}